const isBlob = (value: unknown): value is Blob => value instanceof Blob
const isFile = (value: unknown): value is File => value instanceof File
const isBoolean = (value: unknown): value is boolean =>
    typeof value === 'boolean'
const isNull = (value: unknown): value is null => value === null
const isUndefined = (value: unknown): value is undefined => value === undefined
const isObject = (value: unknown): value is object =>
    value !== null && (typeof value === 'object' || typeof value === 'function')
const isArray = (value: unknown): value is unknown[] => Array.isArray(value)

interface Options {
    arrayIndexes: boolean
    excludeNull: boolean
    useDotSeparator: boolean
    useBrackets: boolean
    booleanAsNumbers: boolean
}

const processData = (
    value: unknown,
    options: Options,
    formData: FormData,
    parent?: string
): void => {
    const processedKey = parent || ''

    if (isNull(value) || isUndefined(value)) {
        if (!options.excludeNull) {
            formData.append(processedKey, '')
        }
        return
    }

    if (isFile(value) || isBlob(value)) {
        formData.append(processedKey, value)
        return
    }

    if (isArray(value)) {
        value.forEach((item, index) => {
            let computedKey = processedKey
            if (options.useBrackets) {
                computedKey += `[${options.arrayIndexes ? index : ''}]`
            }
            processData(item, options, formData, computedKey)
        })
        return
    }

    if (isObject(value)) {
        Object.entries(value as Record<string, unknown>).forEach(
            ([key, data]) => {
                let computedKey = key
                if (parent) {
                    computedKey = options.useDotSeparator
                        ? `${parent}.${key}`
                        : `${parent}[${key}]`
                }
                processData(data, options, formData, computedKey)
            }
        )
        return
    }

    if (isBoolean(value)) {
        formData.append(
            processedKey,
            options.booleanAsNumbers
                ? Number(value).toString()
                : value
                  ? 'true'
                  : 'false'
        )
        return
    }

    formData.append(processedKey, String(value))
}

const defaultOptions: Options = {
    arrayIndexes: true,
    excludeNull: true,
    useDotSeparator: false,
    useBrackets: true,
    booleanAsNumbers: false,
}

const objectToFormData = (
    payload: unknown,
    options: Partial<Options> = {},
    formData: FormData = new FormData()
): FormData => {
    if (!payload) return formData

    const mergedOptions = { ...defaultOptions, ...options }

    processData(payload, mergedOptions, formData)

    return formData
}

export default objectToFormData
